import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import { get } from 'lodash';
import {
  Layout,
  Typography,
  Row,
  Col,
  message,
  Modal,
  Divider,
  Input,
  Radio,
  Checkbox,
  Upload,
  Form,
} from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';

import { ModalContent } from './modal-content';
import PdButton from 'components/pd-button';
import { DisplayImage } from 'components/displayImage';

import {
  convertNumberToRupiahCurrency,
  errHandling,
  formatStatus,
  generateArrayWithBaseObject,
  priceFormat,
} from 'utils';
import OrderService from 'services/order';
import RentService from 'services/rent';
import ReturnService from 'services/return';
import ExchangeService from 'services/exchange';
import UploadService from 'services/upload';
import './style.scss';
import UploadPreview from 'components/upload-preview';
import QuantityItem from 'components/quantity-item';
import orderRefund from 'services/order-refund';
import { priceInputNumberFormatter } from 'utils/inputNumber';

const { Content } = Layout;
const { confirm } = Modal;
const { Title, Text } = Typography;

const BUTTON_LABEL = {
  DEFAULT: '',
  accept_exchange: 'Accept Exchange',
  deliver_exchange: 'Deliver Exchange',
  delivered: 'Completed',
  done_inspection: 'Process Refund',
  exchange_delivered: 'Delivered',
  finish_rent: 'Finish Rent',
  refund_rent: 'Refund Rent',
  reject_exchange: 'Reject Exchange',
  resume_rent: 'Resume Rent',
  return_processed: 'Send to Inspection',
};

const STATUS_LIST = {
  DELIVERED: 'delivered',
  DONE_INSPECTION: 'done_inspection',
  EXCHANGE_ACCEPTED: 'exchange_accepted',
  EXCHANGE_ON_DELIVERY: 'exchange_on_delivery',
  EXCHANGE_REJECTED: 'exchange_rejected',
  EXCHANGE_REQUEST: 'exchange_request',
  REFUND_PROCESSED: 'refund_processed',
  RETURN_PROCESSED: 'return_processed',
};

const FIELDS = {
  ADDRESS: 'address',
  COURIER: 'courier',
  DELIVERED_AT: 'delivered_at',
  DEPOSIT: 'total_deposit',
  FORMATTED_ID: 'formatted_id',
  FORMAT_PACKAGE_ID: 'format_package_id',
  FORMAT_RETURN_ID: 'format_return_id',
  ID: 'id',
  INVOICE_NUMBER: 'invoice_number',
  NET_PRICE: 'net_price',
  ORDERED_AT: 'ordered_at',
  ORDER_PRODUCTS: 'order_products',
  PAID: 'paid',
  PAYMENT_CHANNEL: 'payment_channel',
  PAYMENT_METHOD: 'payment_method',
  PAYMENT_TYPE: 'payment_type',
  PROMO: 'promo',
  PROMO_DISCOUNT: 'promo_discount',
  RENT_DURATION: 'rent_duration.duration',
  RETURN_COST: 'return_cost',
  SHIPPING_COST: 'shipping_cost',
  SHIPPING_METHOD: 'shipping_method',
  STATUS: 'status',
  TAX: 'tax',
  TOTAL_PRICE: 'total_price',
  TRACE_NUMBER: 'trace_number',
  TRACKING_CODE: 'tracking_code',
  USER: 'user',
  WAREHOUSE: 'warehouse',
  PRICE_BEFORE_TAX: 'total_before_tax',
  WALLET_REDUCTION: 'wallet_reduction',
};

const ADDRESS_FIELDS = {
  ADDRESS: 'address',
  SECOND_LINE_ADDRESS: 'second_line_address',
  CITY: 'city',
  PROVINCE: 'province',
  ZIP_CODE: 'zip_code',
  NOTES: 'notes',
  RECIPIENT: 'recipient',
  PHONE_NUMBER: 'phone_number',
};

const USER_FIELDS = {
  NAME: 'first_name',
  PHONE_NUMBER: 'phone_number',
};

const COURIER_FIELDS = {
  TITLE: 'title',
};

const ACTIVITY_FIELDS = {
  ACTOR: 'actor',
  VERB: 'verb',
  CREATED_AT: 'created_at',
  MESSAGE: 'message',
};

const ACTOR_FIELDS = {
  FULL_NAME: 'full_name',
};

const PROMO_FIELDS = {
  CODE: 'code',
};

const WAREHOUSE_FIELDS = {
  TITLE: 'title',
};

const MODAL_TYPE = {
  CHARGE: 'charge',
  ENTER_TRACKING: 'enter_tracking',
  REJECT_ORDER: 'reject_order',
  REJECT_RETURN: 'reject_return',
  RETURN: 'return',
  TRACKING_ORDER: 'tracking_order',
  UPDATE_TRACKING: 'update_tracking',
};

const MODAL_TITLE = {
  CHARGE: 'Charge Penalty',
  ENTER_TRACKING: 'Enter Tracking Code',
  REJECT_ORDER: 'Reject Order',
  REJECT_RETURN: 'Reject Return',
  RETURN: 'Return',
  TRACKING_ORDER: 'Tracking Order',
  UPDATE_TRACKING: 'Enter Tracking Code',
};

const SHOW_LATE_PENALTY_FIELD_CONDITION = [
  'awaiting_inspection',
  'check_inspection',
  'done_inspection',
  'late',
  'ongoing_inspection',
  'refund_processed',
  'rent_returned',
  'return_processed',
];

const SHOW_DAMAGE_PENALTY_AND_DEPOSIT_RETURNED_TO_CUSTOMER = [
  'done_inspection',
  'rent_returned',
  'refund_processed',
];

const refundOptions = [
  {
    label: 'Installation',
    value: 'installation',
  },
  {
    label: 'Product',
    value: 'product',
  },
];

const refundInitialValue = {
  reason: '',
  product_id: 0,
  quantity: 1,
  price: 0,
  installation_fee: 0,
  refund_images_attributes: [
    {
      photo_url: '',
    },
    {
      photo_url: '',
    },
    {
      photo_url: '',
    },
  ],
  video_url: '',
  refund_method: '',
};

const exchangeInitialValue = {
  exchange_reason: '',
  product_id: 0,
  quantity: 1,
  exchange_images_attributes: [
    {
      photo_url: '',
    },
    {
      photo_url: '',
    },
    {
      photo_url: '',
    },
  ],
  video_url: '',
};

function DetailPackage({
  confirmDelivery,
  createUpdateTrackHandler,
  findDriver,
  getOrder,
  getTracking,
  handleShipOrder,
  id,
  isReturn = false,
  isShipment = false,
  liveTracking,
  loadingModal = false,
  loadingPrint = false,
  orderActivity = [],
  printInvoice,
  setLoadingModal,
  setShowModal,
  showModal,
  tracking = [],
  value,
}) {
  const [form] = Form.useForm();
  const [exchangeImageSrc, setExchangeImageSrc] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState('');
  const [modalValue, setModalValue] = useState({});
  const [rentDue, setRentDue] = useState('');
  const [returnRentActivites, setReturnRentActivites] = useState([]);
  const [showFullExchangeImage, setShowFullExchangeImage] = useState(false);
  const [openAcceptOfflinePayment, setOpenAcceptOfflinePayment] =
    useState(false);
  const [paymentType, setPaymentType] = useState('QRIS');
  const [trackNumber, setTrackNumber] = useState('');
  const [errorTrackNumber, setErrorTrackNumber] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [acceptLoading, setAcceptLoading] = useState(false);
  const radioStyle = {
    display: 'block',
    height: '30px',
    lineHeight: '30px',
  };
  const [modalExchange, setModalExchange] = useState(false);
  const [modalRefund, setModalRefund] = useState(false);
  const [modalBankAccount, setModalBankAccount] = useState(false);
  const [selectedProductExchange, setSelectedProductExchange] = useState(
    generateArrayWithBaseObject(
      exchangeInitialValue,
      get(value, 'order_products', []).length
    )
  );

  const [selectedProductRefund, setSelectedProductRefund] = useState(
    generateArrayWithBaseObject(
      refundInitialValue,
      get(value, 'order_products', []).length
    )
  );

  const [bankAccount, setBankAccount] = useState({});

  const {
    late_penalty = 0,
    damage_penalty = 0,
    total_deposit = 0,
  } = value || {};

  const getInvoice = async () => {
    const { data } = await OrderService.getInvoiceOrder(id);
    setInvoiceUrl(data.data.invoice_label_url);
  };

  useEffect(() => {
    if (get(value, 'type', 'buy') === 'rent') {
      let days = get(value, 'rent_duration.duration', 0);
      let result = new Date(get(value, 'delivered_at', ''));
      let formated = formatDate(result.setDate(result.getDate() + days));
      setRentDue(formated);
    }
  }, [value]);

  useEffect(() => {
    if (get(value, 'type', 'buy') === 'rent') {
      getReturnOrderActivity();
    }

    setModalValue({ ...modalValue, tracking_code: value.tracking_code });
    getInvoice();
    // eslint-disable-next-line
  }, [id]);

  function formatDate(str) {
    return dayjs(str).format('DD/MM/YYYY');
  }

  function formatTime(str) {
    return dayjs(str).format('LT');
  }

  function formatMessage(str) {
    if (str) {
      return 'Note: ' + str.charAt(0).toUpperCase() + str.slice(1);
    }
  }

  function formatAddress(obj) {
    let address = `${get(obj, ADDRESS_FIELDS.CITY, '')}, ${get(
      obj,
      ADDRESS_FIELDS.PROVINCE,
      ''
    )} ${get(obj, ADDRESS_FIELDS.ZIP_CODE, '')}`;
    return address;
  }

  function handleConfirmDelivery() {
    confirm({
      title: 'Are you sure the order has been delivered to the customer?',
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk() {
        confirmDelivery();
      },
      onCancel() {},
    });
  }

  function handleConfirmLate() {
    confirm({
      title: 'Mark this order as Late?',
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk() {
        markLate();
      },
      onCancel() {},
    });
  }

  async function processOrder() {
    if (id) {
      try {
        await OrderService.processOrder(id);
        getOrder();
      } catch (err) {
        errHandling(err);
      }
    }
  }

  async function completeOrder() {
    if (id) {
      try {
        await OrderService.completeOrder(id);
        getOrder();
      } catch (err) {
        errHandling(err);
      }
    }
  }

  const acceptOfflineOrder = async () => {
    const newPayload = {
      trace_number: trackNumber,
      payment_type: paymentType,
    };
    if (['QRIS', 'EDC'].includes(paymentType) && !trackNumber)
      return setErrorTrackNumber('Enter trace number!');
    setAcceptLoading(true);
    if (id)
      await OrderService.acceptOfflineOrder(id, newPayload)
        .then(() => {
          getOrder();
          setOpenAcceptOfflinePayment(false);
          message.success('payment completed!');
        })
        .catch((err) => errHandling(err))
        .finally(() => setAcceptLoading(false));
  };

  async function markLate() {
    if (id) {
      try {
        await RentService.setOrderLate(id);
        getOrder();
      } catch (err) {
        errHandling(err);
      }
    }
  }

  function settingModal(val) {
    if (val === 'TRACKING_ORDER') getTracking(id);
    setModalType(MODAL_TYPE[val]);
    setModalTitle(MODAL_TITLE[val]);
    setShowModal(true);
  }

  const actionHandler = async (key) => {
    try {
      setLoadingModal(true);
      if (key) {
        switch (key) {
          case 'enter_tracking':
            createUpdateTracking('create');
            break;
          case 'update_tracking':
            createUpdateTracking('update');
            break;
          case 'reject_order':
            rejectOrder();
            break;
          case 'accept_shipment':
            acceptShipment();
            break;
          case 'reject_shipment':
            rejectReturnProcess();
            break;
          case 'accept_return':
            acceptReturnProcess();
            break;
          case 'reject_return':
            rejectReturnProcess();
            break;
          case 'charged':
            charged();
            break;
          case 'return_completed':
            completeReturnProcess();
            break;
          case 'cancel':
            setShowModal(false);
            break;
          default:
            setShowModal(false);
            break;
        }
      }
    } catch (err) {
      errHandling(err);
      setShowModal(false);
    } finally {
      setLoadingModal(false);
    }
  };

  const createUpdateTracking = async (key) => {
    if (get(modalValue, 'tracking_code', '') === '') {
      message.error('Tracking code cant be empty!');
    } else {
      createUpdateTrackHandler(key, get(modalValue, 'tracking_code', ''));
    }
  };

  const rejectOrder = async () => {
    try {
      await OrderService.rejectOrder(id, get(modalValue, 'reason', ''));
      setShowModal(false);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  };

  const acceptShipment = async () => {
    try {
      await ReturnService.acceptShipmentReturn(id);
      message.success('Shipment has been confirmed');
      setShowModal(false);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  };

  const acceptReturnProcess = async () => {
    try {
      await ReturnService.acceptReturn(id);
      message.success('Return process has been accepted');
      setShowModal(false);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  };

  const rejectReturnProcess = async () => {
    try {
      await ReturnService.rejectReturn(id, {
        message: get(modalValue, 'reason', ''),
      });
      message.success('Return process has been rejected');
      setShowModal(false);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  };

  const charged = async () => {
    if (!get(modalValue, 'charge_amount', 0))
      message.error('Please insert a valid charge price');
    else {
      try {
        let payload = {
          amount: get(modalValue, 'charge_amount', 0),
          message: get(modalValue, 'reason', ''),
          images: get(modalValue, 'image_list', []),
        };
        await ReturnService.setOrderCharged(id, payload);
        message.success('Return has been charged');
        setShowModal(false);
        getOrder();
      } catch (error) {
        errHandling(error);
      }
    }
  };

  const completeReturnProcess = async () => {
    try {
      await ReturnService.setReturnCompleted(id);
      message.success('Return process has been completed');
      setShowModal(false);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  };

  async function getReturnOrderActivity() {
    try {
      const { data: { data: { activities = [] } = {} } = {} } =
        await ReturnService.getOrderActivity(id);
      setReturnRentActivites(activities);
    } catch (error) {
      errHandling(error);
    }
  }

  async function sendToInspection(orderId) {
    try {
      await RentService.sentToInspection(orderId);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  }

  async function processRefund(orderId) {
    try {
      await RentService.processRefund(orderId);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  }

  async function acceptExchange() {
    try {
      await ExchangeService.acceptExchange(id);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  }

  async function rejectExchange() {
    try {
      await ExchangeService.rejectExchange(id);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  }

  async function deliverExchange() {
    try {
      await ExchangeService.deliverExchange(id);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  }

  // after user clicked Delivered-Button, resume rent
  async function resumeRentExchange() {
    try {
      await ExchangeService.resumeRent(id);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  }

  async function refundRentExchange() {
    try {
      await ExchangeService.refundRentExchange(id);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  }

  async function sendToPickUp() {
    try {
      await OrderService.sendToPickUp(id);
      getOrder();
      message.success('successfully send to pickup');
    } catch (error) {
      errHandling(error);
    }
  }

  async function handoverPickup() {
    try {
      await OrderService.handoverPickup(id);
      getOrder();
      message.success('successfully handover to customer');
    } catch (error) {
      errHandling(error);
    }
  }

  const trackingNumber = get(value, FIELDS.TRACKING_CODE, '');
  const getCurrentOrderstatus = () => {
    const _status = get(value, FIELDS.STATUS, '');
    return _status === 'due_soon' ? 'nearly_expired' : _status;
  };

  const buttons = {
    [STATUS_LIST.DONE_INSPECTION]: (
      <PdButton
        className="button-detail"
        onClick={processRefund.bind(this, value[FIELDS.ID])}
        type="primary">
        {BUTTON_LABEL[STATUS_LIST.DONE_INSPECTION]}
      </PdButton>
    ),
    [STATUS_LIST.DELIVERED]: (
      <PdButton
        className="button-detail"
        onClick={completeOrder}
        type="primary">
        {BUTTON_LABEL[STATUS_LIST.DELIVERED]}
      </PdButton>
    ),
    [STATUS_LIST.EXCHANGE_REQUEST]: get(value, 'type', '') === 'rent' && (
      <>
        <PdButton
          className="button-detail"
          onClick={acceptExchange}
          type="primary">
          {BUTTON_LABEL.accept_exchange}
        </PdButton>
        <PdButton
          className="button-detail"
          onClick={rejectExchange}
          outline
          type="danger">
          {BUTTON_LABEL.reject_exchange}
        </PdButton>
      </>
    ),
    [STATUS_LIST.EXCHANGE_ACCEPTED]: (
      <PdButton
        className="button-detail"
        onClick={deliverExchange}
        type="primary">
        {BUTTON_LABEL.deliver_exchange}
      </PdButton>
    ),
    [STATUS_LIST.EXCHANGE_ON_DELIVERY]: (
      <PdButton
        className="button-detail"
        onClick={resumeRentExchange}
        type="primary">
        {BUTTON_LABEL.exchange_delivered}
      </PdButton>
    ),
    [STATUS_LIST.EXCHANGE_REJECTED]: (
      <>
        <PdButton
          className="button-detail"
          onClick={resumeRentExchange}
          type="primary">
          {BUTTON_LABEL.resume_rent}
        </PdButton>
        <PdButton
          className="button-detail"
          onClick={refundRentExchange}
          type="primary">
          {BUTTON_LABEL.refund_rent}
        </PdButton>
      </>
    ),
    [STATUS_LIST.RETURN_PROCESSED]: returnRentActivites.some(
      ({ verb } = {}) => verb === 'confirm_return_shipment'
    ) ? (
      <PdButton
        className="button-detail"
        onClick={sendToInspection.bind(this, value[FIELDS.ID])}
        type="primary">
        {BUTTON_LABEL[STATUS_LIST.RETURN_PROCESSED]}
      </PdButton>
    ) : null,
    DEFAULT: <> </>,
  };

  function handleOnClikExchangeImg(imgSrc) {
    setExchangeImageSrc(imgSrc);
    setShowFullExchangeImage(true);
  }
  function handleOnCloseExchangeImg() {
    setExchangeImageSrc('');
    setShowFullExchangeImage(false);
  }

  const depositReturnedToCustomer = [+late_penalty, +damage_penalty].reduce(
    (acc, curr) => acc - curr,
    +total_deposit
  );

  const showViewReturnButton =
    (returnRentActivites.length > 0 || value['rent_exchange'] === undefined) &&
    [
      'awaiting_inspection',
      'charged',
      'check_inspection',
      'done_inspection',
      'ongoing_inspection',
      'refund_processed',
      'reject_return',
      'rent_returned',
      'return_failed',
      'return_processed',
    ].includes(get(value, FIELDS.STATUS, ''));

  const showFinishRentButton =
    (returnRentActivites.some(({ verb, message_data = {} } = {}) => {
      // possible to get null value from BE, cannot destructure null value with default parameter
      const { account_number } = message_data || {};
      return verb && account_number;
    }) ||
      // if deposit amount less than equal zero, admin dont have to wait for customer to enter their bank account
      depositReturnedToCustomer <= 0) &&
    STATUS_LIST.REFUND_PROCESSED === get(value, FIELDS.STATUS, 'DEFAULT');

  function getRemainingDays() {
    return +get(value, 'time_remaining', 0);
  }

  function isExchangeRejectedThenRentResumed() {
    let isExchangeRejected, isRentResumed;
    orderActivity.forEach(({ verb } = {}) => {
      if (verb === 'reject_exchange') isExchangeRejected = true;
      if (verb === 'resume_rent') isRentResumed = true;
    });
    return isExchangeRejected && isRentResumed;
  }

  function getTotalRemainingDaysIDR() {
    return +get(value, 'total_refund', 0);
  }

  function getTotalRefund() {
    // normal return or exchange rejected then rent resumed
    if (
      isExchangeRejectedThenRentResumed() ||
      value['rent_exchange'] === undefined
    ) {
      return depositReturnedToCustomer > 0 ? depositReturnedToCustomer : 0;
    }

    // exchange rejected then refund rent, calculate prorate
    return getTotalRemainingDaysIDR() + +total_deposit;
  }

  const exchangeOrder = async (values) => {
    try {
      // get checked product
      const checkedProduct = selectedProductExchange.filter(
        (product) => product.product_id !== 0
      );

      // validate image & video
      for (const data of checkedProduct) {
        const truthyPhotoCount = data.exchange_images_attributes.reduce(
          (count, image) => count + (image.photo_url ? 1 : 0),
          0
        );

        if (truthyPhotoCount === 0) return message.error('Please input image');

        if (!data.video_url) return message.error('Please input video');
      }

      const newExchangeProduct = checkedProduct.map((item) => {
        const updatedFile = item.exchange_images_attributes.filter(
          (h) => h.photo_url
        );
        return { ...item, exchange_images_attributes: updatedFile };
      });

      const payload = {
        exchange: {
          exchange_method: values.exchange_method,
          exchange_products_attributes: newExchangeProduct,
        },
      };
      await ExchangeService.exchangeOrder(id, payload);
      message.success('success exchange!');
      setModalExchange(false);
      getOrder();
    } catch (error) {
      errHandling(error);
    }
  };

  const nextStepRefund = (e) => {
    e.preventDefault();

    // get checked product
    const checkedProduct = selectedProductRefund.filter(
      (product) => product.product_id !== 0
    );

    // validate image & video
    for (const data of checkedProduct) {
      const truthyPhotoCount = data.refund_images_attributes.reduce(
        (count, image) => count + (image.photo_url ? 1 : 0),
        0
      );

      if (truthyPhotoCount === 0) return message.error('Please input image');

      if (!data.video_url) return message.error('Please input video');
    }

    setModalBankAccount(true);
    setModalRefund(false);
  };
  const refundOrder = (e) => {
    e.preventDefault();

    if (Object.keys(bankAccount).length !== 3)
      return message.error('Please input all field!');

    for (let key in bankAccount) {
      if (
        typeof bankAccount[key] === 'string' &&
        bankAccount[key].trim() === ''
      )
        return message.error('Please input all field!');
    }
    // get checked product
    const checkedProduct = selectedProductRefund.filter(
      (product) => product.product_id !== 0
    );

    const newRefundProduct = checkedProduct.map((item) => {
      delete item.installation_fee;
      delete item.price;

      const updatedFile = item.refund_images_attributes.filter(
        (h) => h.photo_url
      );

      const newRefundMethod =
        item.refund_method.length === 2
          ? 'product_and_installation'
          : item.refund_method.toString();

      return {
        ...item,
        refund_images_attributes: updatedFile,
        refund_method: newRefundMethod,
      };
    });

    const payload = {
      refund: {
        ...bankAccount,
        refund_products_attributes: newRefundProduct,
      },
    };

    orderRefund.createRefund(id, payload).then(() => {
      message.success('success refund!');
      setModalRefund(false);
      setModalBankAccount(false);
      getOrder();
    });
  };

  const onSelectProductExchange = (
    type = 'single',
    productId,
    index,
    item = {}
  ) => {
    let newSelectedProductExchange = modalRefund
      ? [...selectedProductRefund]
      : [...selectedProductExchange];
    const isExist =
      newSelectedProductExchange.findIndex(
        (product) => product.product_id === productId
      ) !== -1;

    if (isExist && type === 'single') {
      newSelectedProductExchange[index].product_id = 0;
    } else if (productId && type === 'all') {
      value.order_products.forEach((_product, index) => {
        newSelectedProductExchange[index].product_id = 0;
      });
    } else if (type === 'single') {
      newSelectedProductExchange[index].product_id = productId;
      if (modalRefund) {
        newSelectedProductExchange[index].price = item.price;
        newSelectedProductExchange[index].installation_fee =
          item.installation_fee;
      }
    } else {
      value.order_products.forEach((product, index) => {
        newSelectedProductExchange[index].product_id = product.product.id;
        if (modalRefund) {
          newSelectedProductExchange[index].price = product.price;
          newSelectedProductExchange[index].installation_fee =
            product.installation_fee;
        }
      });
    }

    if (modalRefund) {
      setSelectedProductRefund(newSelectedProductExchange);
    } else {
      setSelectedProductExchange(newSelectedProductExchange);
    }
  };

  async function awsUpload(file, uploadURL) {
    try {
      await UploadService.uploadImage(file, uploadURL.upload_url);
      message.success('Successfully uploaded');
    } catch (err) {
      errHandling(err);
    }
  }

  async function beforeUpload(file, type, index, imageIndex) {
    try {
      const ext = file.type.split('/')[1];
      const newSelectedProductExchange = modalRefund
        ? JSON.parse(JSON.stringify(selectedProductRefund))
        : JSON.parse(JSON.stringify(selectedProductExchange));

      if (type === 'video') {
        if (ext !== 'mp4') {
          throw message.error('You can only upload MP4 file!');
        }
      }
      const { data } = await UploadService.getUploadURL(ext, 'product');
      const uploadURL = data.data;

      await awsUpload(file, uploadURL);

      if (type === 'video') {
        newSelectedProductExchange[index].video_url = uploadURL.secure_url;
      } else {
        if (modalRefund) {
          newSelectedProductExchange[index].refund_images_attributes[
            imageIndex
          ].photo_url = uploadURL.secure_url;
        } else {
          newSelectedProductExchange[index].exchange_images_attributes[
            imageIndex
          ].photo_url = uploadURL.secure_url;
        }
      }

      if (modalRefund) {
        setSelectedProductRefund(newSelectedProductExchange);
      } else {
        setSelectedProductExchange(newSelectedProductExchange);
      }
    } catch (err) {
      errHandling(err);
    }
  }

  const onChangeBankAccount = (e) => {
    setBankAccount({
      ...bankAccount,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Content>
      {/* Exchange modal */}
      <Modal
        width={600}
        title="Exchange"
        open={modalExchange}
        onCancel={() => setModalExchange(false)}
        footer={null}>
        <div>
          <Form form={form} onFinish={exchangeOrder}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Text strong>Order No</Text>
              </Col>
              <Col span={18}>
                <Text strong>{get(value, 'formatted_id', '')}</Text>
              </Col>
              <Col span={6}>
                <Text strong>Customer Name</Text>
              </Col>
              <Col span={18}>
                <Text strong>
                  {get(value, 'user.first_name')} {get(value, 'user.last_name')}
                </Text>
              </Col>
              <Col span={6}>
                <Text strong>Exchange Method</Text>
              </Col>
              <Col span={18}>
                <Form.Item
                  name="exchange_method"
                  rules={[
                    {
                      required: true,
                      message: 'Please select exchange method!',
                    },
                  ]}>
                  <Radio.Group>
                    <Radio value="change_in_showroom">Change in Showroom</Radio>
                    <Radio value="send_to_warehouse" disabled>
                      Send to Warehouse
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Divider />
            <Text>Which item you want to exchange?</Text>
            <div>
              <Checkbox
                indeterminate={
                  selectedProductExchange.findIndex(
                    (item) => item.product_id !== 0
                  ) !== -1 &&
                  selectedProductExchange.filter(
                    (item) => item.product_id !== 0
                  ).length !== value.order_products.length
                }
                onChange={(e) =>
                  onSelectProductExchange('all', !e.target.checked)
                }
                style={{
                  marginTop: 12,
                }}
                {...(value.order_products && {
                  checked:
                    selectedProductExchange.filter(
                      (item) => item.product_id !== 0
                    ).length === value.order_products.length,
                })}>
                Select all
              </Checkbox>
              {get(value, 'order_products', []).map((item, index) => {
                const { product } = item;
                const { description, title, preview, price, id } = product;
                return (
                  <React.Fragment key={index}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 12,
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                        }}>
                        <Checkbox
                          onChange={() =>
                            onSelectProductExchange('single', id, index)
                          }
                          checked={
                            selectedProductExchange.findIndex(
                              (exchange) => exchange.product_id === product.id
                            ) !== -1
                          }
                        />
                        <img
                          src={preview.thumbnail_image}
                          alt={title}
                          style={{
                            objectFit: 'cover',
                            width: 64,
                            height: 64,
                            borderRadius: 8,
                          }}
                        />
                        <div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html:
                                description.length > 40
                                  ? description.slice(0, 40) + '...'
                                  : description,
                            }}
                          />
                          <div>
                            {get(item, 'quantity', 0)} product x IDR {price}
                          </div>
                        </div>
                      </div>
                      <QuantityItem
                        maxQuantity={get(item, 'quantity', 0)}
                        onUpdateQuantity={(value) => {
                          let newSelectedProductExchange = [
                            ...selectedProductExchange,
                          ];
                          newSelectedProductExchange[index].quantity = value;
                          setSelectedProductExchange(
                            newSelectedProductExchange
                          );
                        }}
                      />
                    </div>
                    <Row
                      gutter={[12, 12]}
                      style={{
                        marginTop: 12,
                      }}>
                      <Col span={5}>
                        <Text>Input Reason</Text>
                      </Col>
                      <Col span={19}>
                        <Input.TextArea
                          maxLength={360}
                          placeholder="Input your reason (Max: 360 character)"
                          rows={3}
                          onChange={(e) => {
                            const newSelectedProductExchange = [
                              ...selectedProductExchange,
                            ];
                            newSelectedProductExchange[index].exchange_reason =
                              e.target.value;
                            setSelectedProductExchange(
                              newSelectedProductExchange
                            );
                          }}
                        />
                      </Col>
                      <Col span={5}>
                        <Text>Photo</Text>
                      </Col>
                      <Col
                        span={19}
                        style={{
                          display: 'flex',
                          gap: 12,
                        }}>
                        {[1, 2, 3].map((d, child) => (
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={(file) =>
                              beforeUpload(file, 'image', index, child)
                            }
                            key={d}>
                            {get(
                              get(
                                selectedProductExchange[index],
                                'exchange_images_attributes',
                                []
                              )[child],
                              'photo_url',
                              ''
                            ) ? (
                              <img
                                src={
                                  selectedProductExchange[index]
                                    .exchange_images_attributes[child].photo_url
                                }
                                alt={`product-${child}`}
                                width={120}
                                height={120}
                              />
                            ) : (
                              <UploadPreview />
                            )}
                          </Upload>
                        ))}
                      </Col>
                      <Col span={5}>
                        <Text>Video</Text>
                      </Col>
                      <Col span={19}>
                        <Upload
                          accept="video/mp4"
                          showUploadList={false}
                          beforeUpload={(file) =>
                            beforeUpload(file, 'video', index)
                          }>
                          <div
                            style={{
                              padding: '8px 16px',
                              border: '1px solid #3D49A5',
                              borderRadius: 12,
                            }}>
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                color: '#3D49A5',
                                alignItems: 'center',
                              }}>
                              <CloudUploadOutlined />
                              Upload Video
                            </div>
                            {get(
                              selectedProductExchange[index],
                              'video_url',
                              ''
                            ) && (
                              <video
                                src={selectedProductExchange[index].video_url}
                                width={300}
                                height={250}
                                autoPlay
                                controls
                              />
                            )}
                          </div>
                        </Upload>
                      </Col>
                    </Row>
                  </React.Fragment>
                );
              })}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 24,
              }}>
              <PdButton
                onClick={() => setModalExchange(false)}
                outline
                type="primary"
                style={{
                  padding: '0px 16px',
                }}>
                Cancel
              </PdButton>
              <PdButton
                disabled={
                  selectedProductExchange.filter(
                    (product) => product.product_id !== 0
                  ).length === 0
                }
                htmlType="submit"
                type="primary"
                style={{
                  padding: '0px 16px',
                }}>
                Exchange
              </PdButton>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Refund modal */}
      <Modal
        width={600}
        title="Refund"
        open={modalRefund}
        onCancel={() => setModalRefund(false)}
        footer={null}>
        <div>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Text strong>Order No</Text>
            </Col>
            <Col span={18}>
              <Text strong>{get(value, 'formatted_id', '')}</Text>
            </Col>
            <Col span={6}>
              <Text strong>Customer Name</Text>
            </Col>
            <Col span={18}>
              <Text strong>
                {get(value, 'user.first_name')} {get(value, 'user.last_name')}
              </Text>
            </Col>
          </Row>
          <Divider />
          <Text>Which item you want to refund?</Text>
          <div>
            <Checkbox
              indeterminate={
                selectedProductRefund.findIndex(
                  (item) => item.product_id !== 0
                ) !== -1 &&
                selectedProductRefund.filter((item) => item.product_id !== 0)
                  .length !== value.order_products.length
              }
              onChange={(e) =>
                onSelectProductExchange('all', !e.target.checked)
              }
              style={{
                marginTop: 12,
              }}
              {...(value.order_products && {
                checked:
                  selectedProductRefund.filter((item) => item.product_id !== 0)
                    .length === value.order_products.length,
              })}>
              Select all
            </Checkbox>
            {get(value, 'order_products', []).map((item, index) => {
              const { product } = item;
              const { description, title, preview, price, id } = product;
              return (
                <React.Fragment key={item.product.id}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 12,
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                      }}>
                      <Checkbox
                        onChange={() =>
                          onSelectProductExchange('single', id, index, item)
                        }
                        checked={
                          selectedProductRefund.findIndex(
                            (refund) => refund.product_id === id
                          ) !== -1
                        }
                      />
                      <img
                        src={preview.thumbnail_image}
                        alt={title}
                        style={{
                          objectFit: 'cover',
                          width: 64,
                          height: 64,
                          borderRadius: 8,
                        }}
                      />
                      <div>
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              description.length > 40
                                ? description.slice(0, 40) + '...'
                                : description,
                          }}
                        />
                        <div>
                          {get(item, 'quantity', 0)} product x IDR {price}
                        </div>
                      </div>
                    </div>
                    <QuantityItem
                      maxQuantity={get(item, 'quantity', 0)}
                      onUpdateQuantity={(value) => {
                        let newSelectedProductRefund = [
                          ...selectedProductRefund,
                        ];
                        newSelectedProductRefund[index].quantity = value;
                        setSelectedProductRefund(newSelectedProductRefund);
                      }}
                    />
                  </div>
                  <Row
                    gutter={[12, 12]}
                    style={{
                      marginTop: 12,
                    }}>
                    <Col span={5}>
                      <Text>Refund</Text>
                    </Col>
                    <Col span={19}>
                      <Checkbox.Group
                        options={refundOptions}
                        onChange={(value) => {
                          const newSelectedProductRefund = [
                            ...selectedProductRefund,
                          ];
                          newSelectedProductRefund[index].refund_method = value;
                          setSelectedProductRefund(newSelectedProductRefund);
                        }}
                      />
                    </Col>

                    <Col span={5}>
                      <Text>Input Reason</Text>
                    </Col>
                    <Col span={19}>
                      <Input.TextArea
                        placeholder="Input your reason (Max: 360 character)"
                        rows={3}
                        maxLength={360}
                        onChange={(e) => {
                          const newSelectedProductRefund = [
                            ...selectedProductRefund,
                          ];
                          newSelectedProductRefund[index].reason =
                            e.target.value;
                          setSelectedProductRefund(newSelectedProductRefund);
                        }}
                      />
                    </Col>
                    <Col span={5}>
                      <Text>Photo</Text>
                    </Col>
                    <Col
                      span={19}
                      style={{
                        display: 'flex',
                        gap: 12,
                      }}>
                      {[1, 2, 3].map((d, child) => (
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={(file) =>
                            beforeUpload(file, 'image', index, child)
                          }
                          key={d}>
                          {get(
                            get(
                              selectedProductRefund[index],
                              'refund_images_attributes',
                              []
                            )[child],
                            'photo_url',
                            ''
                          ) ? (
                            <img
                              src={
                                selectedProductRefund[index]
                                  .refund_images_attributes[child].photo_url
                              }
                              alt={`product-${child}`}
                              width={120}
                              height={120}
                            />
                          ) : (
                            <UploadPreview />
                          )}
                        </Upload>
                      ))}
                    </Col>
                    <Col span={5}>
                      <Text>Video</Text>
                    </Col>
                    <Col span={19}>
                      <Upload
                        accept="video/mp4"
                        showUploadList={false}
                        beforeUpload={(file) =>
                          beforeUpload(file, 'video', index)
                        }>
                        <div
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #3D49A5',
                            borderRadius: 12,
                          }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                              color: '#3D49A5',
                              alignItems: 'center',
                            }}>
                            <CloudUploadOutlined />
                            Upload Video
                          </div>
                          {get(
                            selectedProductRefund[index],
                            'video_url',
                            ''
                          ) && (
                            <video
                              src={selectedProductRefund[index].video_url}
                              width={300}
                              height={250}
                              autoPlay
                              controls
                            />
                          )}
                        </div>
                      </Upload>
                    </Col>
                  </Row>
                </React.Fragment>
              );
            })}
          </div>
          <Divider />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="title-refund">Estimated Refund Amount</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div className="title-refund-information">
                Total Product Price
              </div>
              <div className="title-refund-value">
                {priceInputNumberFormatter(
                  selectedProductRefund.reduce((accumulator, product) => {
                    if (
                      product.product_id !== 0 &&
                      product.refund_method.includes('product')
                    )
                      return (
                        accumulator +
                        parseFloat(product.price) * product.quantity
                      );

                    return accumulator;
                  }, 0)
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div className="title-refund-information">
                Total Installations Price{' '}
              </div>
              <div className="title-refund-value ">
                {' '}
                {priceInputNumberFormatter(
                  selectedProductRefund.reduce((accumulator, product) => {
                    if (
                      product.product_id !== 0 &&
                      product.refund_method.includes('installation')
                    )
                      return (
                        accumulator +
                        parseFloat(product.installation_fee) * product.quantity
                      );

                    return accumulator;
                  }, 0)
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div className="title-refund-information">Total Refund Price</div>
              <div className="total-refund-price">
                {priceInputNumberFormatter(
                  selectedProductRefund.reduce((accumulator, product) => {
                    if (
                      product.product_id !== 0 &&
                      product.refund_method.length === 2
                    )
                      return (
                        accumulator +
                        (parseFloat(product.installation_fee) +
                          parseFloat(product.price)) *
                          product.quantity
                      );

                    if (
                      product.product_id !== 0 &&
                      product.refund_method.includes('product')
                    )
                      return (
                        accumulator +
                        parseFloat(product.price) * product.quantity
                      );

                    if (
                      product.product_id !== 0 &&
                      product.refund_method.includes('installation')
                    )
                      return (
                        accumulator +
                        parseFloat(product.installation_fee) * product.quantity
                      );

                    return accumulator;
                  }, 0)
                )}
              </div>
            </div>
          </div>
          <Divider />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 24,
            }}>
            <PdButton
              onClick={() => setModalExchange(false)}
              outline
              type="primary"
              style={{
                padding: '0px 16px',
              }}>
              Cancel
            </PdButton>
            <PdButton
              disabled={
                selectedProductRefund.filter(
                  (product) => product.product_id !== 0
                ).length === 0
              }
              onClick={nextStepRefund}
              type="primary"
              style={{
                padding: '0px 16px',
              }}>
              Next
            </PdButton>
          </div>
        </div>
      </Modal>

      {/* Modal Bank Account */}
      <Modal
        width={600}
        title="Refund"
        open={modalBankAccount}
        onCancel={() => {
          setModalBankAccount(false);
          setModalRefund(true);
        }}
        footer={null}>
        <div className="refund-box">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_7218_6833)">
              <path
                d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20ZM11 7H13V9H11V7ZM11 11H13V17H11V11Z"
                fill="#3F4C9F"
              />
            </g>
            <defs>
              <clipPath id="clip0_7218_6833">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>

          <div>We will use your bank information to send the refund.</div>
        </div>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Text strong>Order No</Text>
          </Col>
          <Col span={18}>
            <Text strong>{get(value, 'formatted_id', '')}</Text>
          </Col>
          <Col span={6}>
            <Text strong>Customer Name</Text>
          </Col>
          <Col span={18}>
            <Text strong>
              {get(value, 'user.first_name')} {get(value, 'user.last_name')}
            </Text>
          </Col>
        </Row>
        <Divider />
        <div></div>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Text strong>Bank Name</Text>
          </Col>
          <Col span={18}>
            <Input
              name="bank_name"
              placeholder="Input your bank name (e.g: BCA,BRI etc)"
              onChange={(event) => onChangeBankAccount(event)}
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Text strong>Bank Account Number</Text>
          </Col>
          <Col span={18}>
            <Input
              placeholder="Input your bank account number"
              type="number"
              onChange={(event) => onChangeBankAccount(event)}
              name="account_number"
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Text strong>Account Holder</Text>
          </Col>
          <Col span={18}>
            <Input
              placeholder="Input account holder name"
              onChange={(event) => onChangeBankAccount(event)}
              name="account_holder"
            />
          </Col>
        </Row>
        <Divider />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 24,
          }}>
          <PdButton
            onClick={() => {
              setModalRefund(true);
              setModalBankAccount(false);
            }}
            outline
            type="primary"
            style={{
              padding: '0px 16px',
            }}>
            Previous
          </PdButton>
          <PdButton
            onClick={refundOrder}
            type="primary"
            style={{
              padding: '0px 16px',
            }}>
            Next
          </PdButton>
        </div>
      </Modal>

      <Modal
        title="Store Payment"
        open={openAcceptOfflinePayment}
        onCancel={() => setOpenAcceptOfflinePayment(false)}
        footer={null}>
        <div>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Text strong>Order No</Text>
            </Col>
            <Col span={18}>
              <Text strong>{value.formatted_id}</Text>
            </Col>
            <Col span={6}>
              <Text strong>Customer Name</Text>
            </Col>
            <Col span={18}>
              <Text strong>
                {get(get(value, FIELDS.USER, {}), USER_FIELDS.NAME, '')}
              </Text>
            </Col>
            <Col span={6}>
              <Text strong>
                Trace Number{' '}
                {['QRIS', 'EDC'].includes(paymentType) && (
                  <span style={{ color: 'red' }}>*</span>
                )}
              </Text>
            </Col>
            <Col span={18}>
              <Input
                onChange={(e) => setTrackNumber(e.target.value)}
                placeholder="Enter trace number"
              />
              <div style={{ color: 'red' }}>{errorTrackNumber}</div>
            </Col>
            <Col span={6}>
              <Text strong>Payment Type</Text>
            </Col>
            <Col span={18}>
              <Radio.Group
                onChange={(e) => {
                  if (e.target.value === 'CASH') {
                    setErrorTrackNumber('');
                  }
                  setPaymentType(e.target.value);
                }}
                value={paymentType}>
                <Radio style={radioStyle} value="QRIS">
                  QRIS
                </Radio>
                <Radio style={radioStyle} value="EDC">
                  Regular EDC
                </Radio>
                <Radio style={radioStyle} value="CASH">
                  CASH
                </Radio>
              </Radio.Group>
            </Col>
          </Row>
          <div className="accept-offline">
            <PdButton
              loading={acceptLoading}
              onClick={acceptOfflineOrder}
              className="button-detail"
              type="primary">
              Process Order
            </PdButton>
          </div>
        </div>
      </Modal>
      <DisplayImage
        show={showFullExchangeImage}
        imageSrc={exchangeImageSrc}
        onClose={handleOnCloseExchangeImg}
      />
      <Modal
        title={modalTitle}
        open={showModal}
        onCancel={setShowModal.bind(this, false)}
        confirmLoading={loadingModal}
        footer={null}
        width={modalType === 'tracking_order' ? 800 : 600}>
        <ModalContent
          actionHandler={actionHandler.bind(this)}
          addressFields={ADDRESS_FIELDS}
          courierFields={COURIER_FIELDS}
          fields={FIELDS}
          liveTracking={liveTracking}
          loadingModal={loadingModal}
          modalType={modalType}
          modalValue={modalValue}
          rentDue={rentDue}
          setModalValue={setModalValue}
          tracking={tracking}
          userFields={USER_FIELDS}
          value={value}
          trackingNumber={trackingNumber}
          settingModal={settingModal}
          isShipment={isShipment}
        />
      </Modal>

      <Row>
        <Col span={24} className="section-container">
          <div className=" inner-section">
            <div className="summaryContent">
              <Row>
                <Col span={24}>
                  <Title className="pageSubtitle" level={3}>
                    Order Summary
                  </Title>
                </Col>
                <Col span={18}>
                  <Row>
                    {get(value, FIELDS.INVOICE_NUMBER, '') && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong className="">
                            Invoice Number
                          </Text>
                        </Col>
                        <Col span={17}>
                          <Text className="pd-color-primary" strong>
                            <a
                              href={invoiceUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer">
                              {get(value, FIELDS.INVOICE_NUMBER, '')}
                            </a>
                          </Text>
                        </Col>
                      </Col>
                    )}
                    {value.offline && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong className="">
                            Trace Number{' '}
                          </Text>
                        </Col>
                        <Col span={17}>
                          <Text className="pd-color-primary" strong>
                            {get(value, FIELDS.TRACE_NUMBER, '')}
                          </Text>
                        </Col>
                      </Col>
                    )}

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong className="">
                          Order No.
                        </Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {get(value, FIELDS.FORMATTED_ID, '')}
                        </Text>
                      </Col>
                    </Col>

                    {get(value, FIELDS.FORMAT_RETURN_ID, '') && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong className="">
                            Return ID
                          </Text>
                        </Col>
                        <Col span={17}>
                          <Text type="secondary">
                            {get(value, FIELDS.FORMAT_RETURN_ID, '')}
                          </Text>
                        </Col>
                      </Col>
                    )}

                    {get(value, 'rent_duration', '') && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong className="">
                            Rent Duration
                          </Text>
                        </Col>
                        <Col span={17}>
                          <Text type="secondary">
                            {get(value, FIELDS.RENT_DURATION, '')} Days
                          </Text>
                        </Col>
                      </Col>
                    )}

                    {get(value, 'rent_duration', '') &&
                      get(value, 'delivered_at', '') && (
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong className="">
                              Rent Due
                            </Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">{rentDue}</Text>
                          </Col>
                        </Col>
                      )}

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Status</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {formatStatus(getCurrentOrderstatus())}
                        </Text>
                      </Col>
                    </Col>

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Transaction Date</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {`${formatDate(
                            get(value, FIELDS.ORDERED_AT, '')
                          )} - ${formatTime(
                            get(value, FIELDS.ORDERED_AT, '')
                          )}`}
                        </Text>
                      </Col>
                    </Col>

                    {get(value, FIELDS.PAYMENT_METHOD, '') && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong>Payment Method</Text>
                        </Col>
                        <Col span={17}>
                          {value.offline ? (
                            <Text type="secondary">Store Payment</Text>
                          ) : (
                            <Text type="secondary">
                              {get(value, FIELDS.PAYMENT_METHOD, '')} -
                              {get(value, FIELDS.PAYMENT_CHANNEL, '')}
                            </Text>
                          )}
                        </Col>
                      </Col>
                    )}

                    {value.offline && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong>Store Payment </Text>
                        </Col>
                        <Col span={17}>
                          <Text type="secondary">
                            {get(value, FIELDS.PAYMENT_TYPE, '-')}
                          </Text>
                        </Col>
                      </Col>
                    )}

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Customer Name</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {get(
                            get(value, FIELDS.USER, {}),
                            USER_FIELDS.NAME,
                            ''
                          )}
                        </Text>
                      </Col>
                    </Col>

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Customer Phone</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {get(
                            get(value, FIELDS.USER, {}),
                            USER_FIELDS.PHONE_NUMBER,
                            ''
                          )}
                        </Text>
                      </Col>
                    </Col>

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Recipient Name</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {get(
                            get(value, FIELDS.ADDRESS, {}),
                            ADDRESS_FIELDS.RECIPIENT,
                            ''
                          ) || get(value, ADDRESS_FIELDS.RECIPIENT, '')}
                        </Text>
                      </Col>
                    </Col>
                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Recipient Phone</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {get(
                            get(value, FIELDS.ADDRESS, {}),
                            ADDRESS_FIELDS.PHONE_NUMBER,
                            ''
                          ) || get(value, 'recipient_number', '')}
                        </Text>
                      </Col>
                    </Col>

                    <Col span={24}>
                      <Col span={7} style={{ marginBottom: '0' }}>
                        <Text strong>Shipping Address</Text>
                      </Col>
                      <Col span={17} style={{ marginBottom: '0' }}>
                        <Text type="secondary">
                          {get(
                            get(value, FIELDS.ADDRESS, {}),
                            ADDRESS_FIELDS.ADDRESS,
                            ''
                          )}
                        </Text>
                      </Col>
                    </Col>
                    {get(value, FIELDS.ADDRESS, {})[
                      ADDRESS_FIELDS.SECOND_LINE_ADDRESS
                    ] && (
                      <Col span={24}>
                        <Col span={7} style={{ margin: '0' }}></Col>
                        <Col span={17} style={{ margin: '0' }}>
                          <Text type="secondary">
                            {get(
                              get(value, FIELDS.ADDRESS, {}),
                              ADDRESS_FIELDS.SECOND_LINE_ADDRESS,
                              ''
                            )}
                          </Text>
                        </Col>
                      </Col>
                    )}

                    <Col span={24} className="mb-15">
                      <Col span={7} style={{ marginTop: '0' }}></Col>
                      <Col span={17} style={{ marginTop: '0' }}>
                        {Object.keys(value.address || {}).length > 0 && (
                          <Text type="secondary">
                            {formatAddress(get(value, FIELDS.ADDRESS, {}))}
                          </Text>
                        )}
                      </Col>
                    </Col>

                    {get(value, FIELDS.USER, {})[ADDRESS_FIELDS.NOTES] && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong>Shipping Note</Text>
                        </Col>
                        <Col span={17}>
                          <Text type="secondary">
                            {get(
                              get(value, FIELDS.USER, {}),
                              ADDRESS_FIELDS.NOTES,
                              ''
                            )}
                          </Text>
                        </Col>
                      </Col>
                    )}

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Warehouse Loc</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {get(
                            get(value, FIELDS.WAREHOUSE, {}),
                            WAREHOUSE_FIELDS.TITLE,
                            '-'
                          )}
                        </Text>
                      </Col>
                    </Col>

                    {get(value, ['courier', 'title'], null) && (
                      <Col span={24} className="mb-15">
                        <Col span={7}>
                          <Text strong>Courier</Text>
                        </Col>
                        <Col span={17}>
                          <Text type="secondary">
                            {get(
                              get(value, FIELDS.COURIER, {}),
                              COURIER_FIELDS.TITLE,
                              '-'
                            )}
                          </Text>
                        </Col>
                      </Col>
                    )}

                    <Col span={24} className="mb-15">
                      <Col span={7}>
                        <Text strong>Shipping Method</Text>
                      </Col>
                      <Col span={17}>
                        <Text type="secondary">
                          {get(value, FIELDS.SHIPPING_METHOD, '')}
                        </Text>
                      </Col>
                    </Col>
                    {get(value, FIELDS.SHIPPING_METHOD, '') === 'PICKUP' && (
                      <>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Pickup Point</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, 'pickup_point.title', '')}
                            </Text>
                          </Col>
                        </Col>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Pickup Point Address</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, 'pickup_point.address', '')}
                            </Text>
                          </Col>
                        </Col>
                      </>
                    )}

                    {trackingNumber &&
                      !['gosend', 'custom'].includes(
                        get(value, [FIELDS.COURIER, 'code'], null)
                      ) && (
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Tracking Number</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">{trackingNumber}</Text>
                          </Col>
                        </Col>
                      )}
                    {value['rent_exchange'] !== undefined && (
                      <>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Exchange note</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {value['rent_exchange'] &&
                                value['rent_exchange'].note}
                            </Text>
                          </Col>
                        </Col>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Exchange Images</Text>
                          </Col>
                          <Col span={17}>
                            {get(value, 'rent_exchange.images', []).map(
                              (imgSrc, idx) => (
                                <img
                                  key={idx}
                                  src={imgSrc}
                                  onClick={handleOnClikExchangeImg.bind(
                                    this,
                                    imgSrc
                                  )}
                                  width={100}
                                  height={100}
                                  alt="order"
                                />
                              )
                            )}
                          </Col>
                        </Col>
                      </>
                    )}
                    {SHOW_LATE_PENALTY_FIELD_CONDITION.includes(
                      get(value, 'status', null)
                    ) && (
                      <>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Total Deposit</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {convertNumberToRupiahCurrency(
                                value.total_deposit
                              )}
                            </Text>
                          </Col>
                        </Col>
                        {!isExchangeRejectedThenRentResumed() &&
                          value['rent_exchange'] !== undefined && (
                            <Col span={24} className="mb-15">
                              <Col span={7}>
                                <Text strong> Total Remaining Days</Text>
                              </Col>
                              <Col span={17}>
                                <Text type="secondary">
                                  ({getRemainingDays()} Days){' '}
                                  {convertNumberToRupiahCurrency(
                                    getTotalRemainingDaysIDR()
                                  )}
                                </Text>
                              </Col>
                            </Col>
                          )}

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Late Penalty</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="danger">
                              {convertNumberToRupiahCurrency(
                                value.late_penalty
                              )}
                            </Text>
                          </Col>
                        </Col>
                        {SHOW_DAMAGE_PENALTY_AND_DEPOSIT_RETURNED_TO_CUSTOMER.includes(
                          get(value, 'status', null)
                        ) && (
                          <>
                            <Col span={24} className="mb-15">
                              <Col span={7}>
                                <Text strong>Damage Penalty</Text>
                              </Col>
                              <Col span={17}>
                                <Text type="danger">
                                  {convertNumberToRupiahCurrency(
                                    value.damage_penalty
                                  )}
                                </Text>
                              </Col>
                            </Col>

                            <Col span={24} className="mb-15">
                              <Col span={7}>
                                <Text strong>Total Refund</Text>
                              </Col>
                              <Col span={17}>
                                <Text type="secondary">
                                  {convertNumberToRupiahCurrency(
                                    getTotalRefund()
                                  )}
                                </Text>
                              </Col>
                            </Col>
                          </>
                        )}
                      </>
                    )}
                  </Row>
                </Col>

                <Col span={6}>
                  <div className="buttonSection">
                    {get(value, FIELDS.STATUS, '') === 'processed' &&
                      get(value, 'shipping_method', null) === 'PICKUP' && (
                        <>
                          <PdButton
                            onClick={sendToPickUp}
                            className="button-detail"
                            type="primary">
                            Send to Pickup 2
                          </PdButton>
                          <PdButton
                            onClick={handoverPickup}
                            className="button-detail"
                            type="primary">
                            Handover to customer
                          </PdButton>
                        </>
                      )}
                    {get(value, FIELDS.STATUS, '') === 'processed' &&
                      get(value, 'shipping_method', null) !== 'PICKUP' && (
                        <>
                          <PdButton
                            onClick={printInvoice}
                            loading={loadingPrint}
                            className="button-detail"
                            type="primary">
                            Print Shipping Label
                          </PdButton>
                          {get(value, [FIELDS.COURIER, 'code'], null) ===
                            'gosend' && (
                            <PdButton
                              onClick={findDriver}
                              className="button-detail"
                              type={isShipment ? 'ghost-primary' : 'primary'}>
                              Find Driver
                            </PdButton>
                          )}
                          {['jne', 'sap', 'custom', 'rpx'].includes(
                            get(value, [FIELDS.COURIER, 'code'], null)
                          ) && (
                            <PdButton
                              className="button-detail"
                              onClick={handleShipOrder}
                              type="primary">
                              Ship Order
                            </PdButton>
                          )}
                          {!['gosend', 'sap'].includes(
                            get(value, [FIELDS.COURIER, 'code'], null)
                          ) && (
                            <PdButton
                              onClick={settingModal.bind(
                                this,
                                'ENTER_TRACKING'
                              )}
                              className="button-detail"
                              type={isShipment ? 'ghost-primary' : 'primary'}>
                              Enter Tracking Code
                            </PdButton>
                          )}
                          {!isShipment && (
                            <PdButton
                              onClick={settingModal.bind(this, 'REJECT_ORDER')}
                              className="button-detail"
                              outline
                              type="danger">
                              Reject Order
                            </PdButton>
                          )}
                        </>
                      )}
                    {get(value, FIELDS.STATUS, '') === 'shipped' && (
                      <>
                        <PdButton
                          onClick={settingModal.bind(this, 'TRACKING_ORDER')}
                          className="button-detail"
                          type="primary">
                          Track Order
                        </PdButton>
                        {!['gosend', 'sap'].includes(
                          get(value, [FIELDS.COURIER, 'code'], null)
                        ) && (
                          <PdButton
                            onClick={settingModal.bind(this, 'UPDATE_TRACKING')}
                            className="button-detail"
                            type="primary">
                            Update Tracking Code
                          </PdButton>
                        )}
                        <PdButton
                          onClick={handleConfirmDelivery}
                          className="button-detail"
                          outline
                          type="primary">
                          Confirm Delivery
                        </PdButton>
                      </>
                    )}

                    {get(value, FIELDS.STATUS, '') === 'payment_verified' && (
                      <>
                        <PdButton
                          onClick={processOrder}
                          className="button-detail"
                          type="primary">
                          Accept Order
                        </PdButton>
                        <PdButton
                          onClick={settingModal.bind(this, 'REJECT_ORDER')}
                          className="button-detail"
                          outline
                          type="danger">
                          Reject Order
                        </PdButton>
                      </>
                    )}
                    {get(value, FIELDS.STATUS, '') === 'waiting_payment' &&
                      value.offline && (
                        <>
                          <PdButton
                            onClick={() => setOpenAcceptOfflinePayment(true)}
                            className="button-detail"
                            type="primary">
                            Payment Complete
                          </PdButton>
                          <PdButton
                            onClick={settingModal.bind(this, 'REJECT_ORDER')}
                            className="button-detail"
                            outline
                            type="danger">
                            Reject Order
                          </PdButton>
                        </>
                      )}
                    {['rented', 'due_soon'].includes(
                      get(value, FIELDS.STATUS, '')
                    ) && (
                      <PdButton
                        onClick={handleConfirmLate}
                        className="button-detail"
                        type="primary">
                        Mark as late
                      </PdButton>
                    )}
                    {showViewReturnButton && (
                      <PdButton
                        className="button-detail"
                        onClick={settingModal.bind(this, 'RETURN')}
                        type="primary">
                        View Return
                      </PdButton>
                    )}

                    {showFinishRentButton && (
                      <PdButton
                        className="button-detail"
                        onClick={completeReturnProcess}
                        type="primary">
                        {BUTTON_LABEL.finish_rent}
                      </PdButton>
                    )}

                    {get(value, FIELDS.STATUS, '') === 'delivered' && (
                      <>
                        <PdButton
                          onClick={() => setModalExchange(true)}
                          className="button-detail"
                          outline
                          type="primary">
                          Exchange
                        </PdButton>
                        <PdButton
                          onClick={() => setModalRefund(true)}
                          className="button-detail"
                          outline
                          type="primary">
                          Refund
                        </PdButton>
                      </>
                    )}

                    {buttons[get(value, FIELDS.STATUS, 'DEFAULT')]}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        <Col span={24} className="section-container">
          <div className="inner-section">
            <Title className="pageSubtitle" level={3}>
              Products
            </Title>
            {(isReturn || isShipment) &&
              get(value, FIELDS.ORDER_PRODUCTS, []).map((op, index) => (
                <div key={index} className="product-list">
                  <Col span={4}>
                    <img
                      src={op.product.preview.secure_url}
                      alt={op.product.preview.title}
                      className="scale-img"
                    />
                  </Col>
                  <Col span={15}>
                    <div>
                      <Text strong>{op.product.title}</Text>
                    </div>
                    <div className="fs-14">
                      <Text>
                        {get(op, 'quantity', 0)}{' '}
                        {get(op, 'quantity', 0) > 1 ? 'products' : 'product'}
                      </Text>
                    </div>

                    {get(value, FIELDS.FORMAT_PACKAGE_ID, '') && (
                      <div className="fs-14">
                        <Text>
                          Package ID: {get(value, FIELDS.FORMAT_PACKAGE_ID, '')}
                        </Text>
                      </div>
                    )}

                    {typeof get(op.color, 'color') !== 'undefined' && (
                      <div className="fs-14">
                        <Text>Color: {get(op.color, 'color')}</Text>
                      </div>
                    )}
                  </Col>
                  <Col span={4}>
                    <Text strong>{get(op, 'quantity', 0)} pcs</Text>
                  </Col>
                </div>
              ))}
            {!isShipment && !isReturn && (
              <>
                {get(value, FIELDS.ORDER_PRODUCTS, []).map((op, index) => (
                  <div key={index} className="product-list">
                    <Col span={4}>
                      <img
                        src={op.product.preview.secure_url}
                        alt={op.product.preview.title}
                        className="scale-img"
                      />
                    </Col>
                    <Col span={13}>
                      <div>
                        <Text strong>{op.product.title}</Text>
                      </div>
                      <div className="fs-14">
                        <Text>SKU : {op.product.sku}</Text>
                      </div>

                      <div className="fs-14">
                        <Text>
                          {get(op, 'quantity', 0)}
                          {get(op, 'quantity', 0) > 1
                            ? 'products'
                            : 'product'}{' '}
                          x IDR {priceFormat(Number(op.price))}
                        </Text>
                      </div>

                      {get(value, FIELDS.FORMAT_PACKAGE_ID, '') && (
                        <div className="fs-14">
                          <Text>
                            {`Package ID: ${get(
                              value,
                              FIELDS.FORMAT_PACKAGE_ID,
                              ''
                            )}`}
                          </Text>
                        </div>
                      )}

                      {op.installation && (
                        <div className="fs-14">
                          <Text>
                            {`Installation Fee: ${priceFormat(
                              op.installation_fee * op.quantity
                            )}`}
                          </Text>
                        </div>
                      )}

                      {typeof get(op.color, 'color') !== 'undefined' && (
                        <div className="fs-14">
                          <Text>Color: {get(op.color, 'color')}</Text>
                        </div>
                      )}
                    </Col>
                    <Col span={4}>
                      <Text strong>
                        IDR{' '}
                        {priceFormat(get(op, 'quantity', 0) * Number(op.price))}
                      </Text>
                      {Number(op.product.discounted_price) > 0 && (
                        <div
                          style={{
                            color: 'red',
                            textDecoration: 'line-through',
                          }}>
                          IDR{' '}
                          {priceFormat(
                            get(op, 'quantity', 0) * Number(op.product.price)
                          )}
                        </div>
                      )}
                    </Col>
                  </div>
                ))}
                <div className="totalSummary">
                  <Row>
                    <Col span={15} className="left">
                      <Text>Total Price</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        IDR {priceFormat(get(value, FIELDS.TOTAL_PRICE, '0'))}
                      </Text>
                    </Col>

                    <Col span={15} className="left">
                      <Text>Wallet Reduction</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        IDR{' '}
                        {priceFormat(get(value, FIELDS.WALLET_REDUCTION, '0'))}
                      </Text>
                    </Col>

                    <Col span={15} className="left">
                      <Text>Deposit</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        IDR {priceFormat(get(value, FIELDS.DEPOSIT, '0'))}
                      </Text>
                    </Col>

                    <Col span={15} className="left">
                      <Text>Shipping Cost</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        {`IDR ${priceFormat(
                          get(value, FIELDS.SHIPPING_COST, '0')
                        )}`}
                      </Text>
                    </Col>
                    <Col span={15} className="left">
                      <Text>Return Cost</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        {`IDR ${priceFormat(
                          get(value, FIELDS.RETURN_COST, '0')
                        )}`}
                      </Text>
                    </Col>
                    {get(value, 'installation_fee') > 0 && (
                      <>
                        <Col span={15} className="left">
                          <Text>Total Installation Cost</Text>
                        </Col>
                        <Col span={4} offset={1}>
                          <Text strong>
                            {`IDR ${priceFormat(
                              get(value, 'installation_fee', 0)
                            )}`}
                          </Text>
                        </Col>
                        <Col span={15} className="left">
                          <Text>Installation Transportation Cost</Text>
                        </Col>
                        <Col span={4} offset={1}>
                          <Text strong>
                            IDR {priceFormat(get(value, 'transport_fee', ''))}
                          </Text>
                        </Col>
                      </>
                    )}

                    {get(value, FIELDS.PROMO_DISCOUNT, '0') !== '0.0' && (
                      <>
                        <Col span={15} className="left">
                          <Text>Promo </Text>
                          <Text className="promo">
                            (
                            {get(
                              value,
                              `${FIELDS.PROMO}.${PROMO_FIELDS.CODE}`,
                              '0'
                            )}
                            )
                          </Text>
                        </Col>
                        <Col span={4} offset={1}>
                          <Text strong className="promo price">
                            {`- IDR
                            ${priceFormat(
                              get(value, FIELDS.PROMO_DISCOUNT, '0')
                            )}`}
                          </Text>
                        </Col>
                      </>
                    )}

                    <Col span={15} className="left">
                      <Text>Subtotal Before PPN</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        {`IDR 
                        ${priceFormat(
                          Math.round(+get(value, FIELDS.PRICE_BEFORE_TAX, '0'))
                        )}`}
                      </Text>
                    </Col>

                    <Col span={15} className="left">
                      <Text>PPN</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        {`IDR 
                        ${priceFormat(
                          Math.round(+get(value, FIELDS.TAX, '0'))
                        )}`}
                      </Text>
                    </Col>
                    <Divider />

                    <Col span={15} className="left">
                      <Text>Total Payment</Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong>
                        IDR {priceFormat(get(value, FIELDS.NET_PRICE, '0'))}
                      </Text>
                    </Col>
                  </Row>
                </div>
              </>
            )}
          </div>
        </Col>

        <Col span={24} className="section-container">
          <div className="inner-section">
            <Title className="pageSubtitle" level={3}>
              Order Status
            </Title>
            {orderActivity.map((activity, index) => (
              <div key={index} className="activityContent">
                <Col span={5}>
                  {get(
                    get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                    ACTOR_FIELDS.FULL_NAME,
                    ''
                  ) === 'system' ? (
                    <div className="activity activity-system">
                      {formatStatus(
                        get(
                          get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                          ACTOR_FIELDS.FULL_NAME,
                          ''
                        )
                      )}
                    </div>
                  ) : get(
                      get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                      ACTOR_FIELDS.FULL_NAME,
                      ''
                    ) === 'customer' ? (
                    <div className="activity activity-customer">
                      {formatStatus(
                        get(
                          get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                          ACTOR_FIELDS.FULL_NAME,
                          ''
                        )
                      )}
                    </div>
                  ) : (
                    <div className="activity activity-admin">
                      {formatStatus(
                        get(
                          get(activity, ACTIVITY_FIELDS.ACTOR, {}),
                          ACTOR_FIELDS.FULL_NAME,
                          ''
                        )
                      )}
                    </div>
                  )}
                </Col>
                <Col span={10} offset={1}>
                  {formatStatus(get(activity, ACTIVITY_FIELDS.VERB, ''))}
                  <br />
                  {formatMessage(get(activity, ACTIVITY_FIELDS.MESSAGE, ''))}
                </Col>
                <Col span={10} className="fs-14">
                  {formatDate(get(activity, ACTIVITY_FIELDS.CREATED_AT, ''))} -{' '}
                  {formatTime(get(activity, ACTIVITY_FIELDS.CREATED_AT, ''))}
                </Col>
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </Content>
  );
}

export default DetailPackage;
